import { Text } from '@oneaudi/unified-web-components';
import { PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';
import { ReactCompareSlider, ReactCompareSliderHandle } from 'react-compare-slider';
import { VisualItem } from '~/app/compositions/content/gallery/items/gallery-items';
import { GalleryItemDefaultStyle } from '~/app/compositions/content/gallery/styles/gallery-style';
import { useI18n } from '~/app/i18n/i18n';
import { ConfiguredCarCatalogImage } from '~/graphql/__generated__/graphql';
import { useConfigurator } from '~/graphql/hooks/use-configurator';

interface CompareGalleryProps extends PropsWithChildren {
	currentMedia: Array<Array<ConfiguredCarCatalogImage>>;
	enableComparison?: boolean;
	aspectRatio?: string;
	index?: number;
	trackComparisonInteraction: () => void;
	activeIndex?: number;
	id: string;
}

export const GalleryCompare = ({
	enableComparison,
	aspectRatio = '4 / 3',
	index = 0,
	currentMedia,
	children,
	trackComparisonInteraction,
	activeIndex,
	id,
}: CompareGalleryProps) => {
	const { i18n } = useI18n();
	const { carIdentifier } = useConfigurator();

	const _id = `compare-handle-${id}-${index}`;
	const toCompareToMedia = useRef<Array<ConfiguredCarCatalogImage[]>>([]);
	const prevMedia = useRef<Array<ConfiguredCarCatalogImage[]>>(currentMedia);
	const carId = JSON.stringify(carIdentifier);
	const prevId = useRef<string>(carId);

	const hasInteracted = useRef<boolean>(false);

	const [position, setPosition] = useState<number>(50);

	useEffect(() => {
		if (carId !== prevId.current) {
			toCompareToMedia.current = prevMedia.current;
			prevMedia.current = currentMedia;
			prevId.current = carId;
		}
	}, [carId, currentMedia]);

	const handleStyles = useMemo(
		() =>
			enableComparison
				? {
						visibility: 'visible' as const,
						position: 'static' as const,
					}
				: {
						visibility: 'hidden' as const,
						position: 'absolute' as const,
					},
		[enableComparison],
	);

	const onPointerUpHandler = () => {
		if (enableComparison && position !== 50 && !hasInteracted.current) {
			hasInteracted.current = true;
			trackComparisonInteraction();
		}
	};

	const onHandleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
		if (!enableComparison) {
			return;
		}

		let newPos = position;
		switch (e.key) {
			case 'Enter':
				e.preventDefault();
				newPos = position !== 100 ? 100 : 0;
				break;
			case 'Home':
				e.preventDefault();
				newPos = 0;
				break;
			case 'End':
				e.preventDefault();
				newPos = 100;
				break;
			case 'PageUp':
				e.preventDefault();
				newPos = Math.min(100, position + 10);
				break;
			case 'PageDown':
				e.preventDefault();
				newPos = Math.max(0, position - 10);
				break;
			default:
				return;
		}

		if (newPos !== position) {
			setPosition(newPos);
			if (newPos !== 50 && !hasInteracted.current) {
				hasInteracted.current = true;
				trackComparisonInteraction();
			}
		}
	};

	useEffect(() => {
		if (activeIndex !== index || !enableComparison) {
			return;
		}
		const el = document.getElementById(_id);
		el?.focus();
	}, [enableComparison, activeIndex, index, _id]);

	return (
		<div
			role="group"
			aria-label={i18n('ngc.a11y.comparison.sliderLabel')}
			style={{ height: '100%', position: 'relative' }}>
			<div
				style={{
					position: 'absolute',
					left: -9999,
					top: 'auto',
					width: 1,
					height: 1,
					overflow: 'hidden',
				}}
				aria-live="polite">
				{i18n('ngc.a11y.comparison.instructions')}
			</div>

			<ReactCompareSlider
				onPointerUp={onPointerUpHandler}
				onPositionChange={(p) => setPosition(Math.round(p))}
				position={position}
				disabled={!enableComparison}
				style={{ height: '100%' }}
				handle={
					<ReactCompareSliderHandle
						linesStyle={{
							background: 'rgba(255, 255, 255, 0.25)',
							boxShadow: 'none',
							width: '1px',
						}}
						buttonStyle={{
							border: 'none',
							backgroundColor: 'rgba(128, 128, 128, 0.5)',
							backdropFilter: 'none',
							boxShadow: 'none',
						}}
						id={_id}
						role="separator"
						aria-orientation="vertical"
						aria-valuemin={0}
						aria-valuemax={100}
						aria-valuenow={position}
						aria-label={i18n('ngc.a11y.comparison.handleLabel')}
						tabIndex={enableComparison ? 0 : -1}
						onKeyDown={onHandleKeyDown}
						style={handleStyles}
					/>
				}
				itemOne={children}
				itemTwo={
					enableComparison ? (
						toCompareToMedia.current && toCompareToMedia.current.length > 0 ? (
							<GalleryItemDefaultStyle $aspectRatio={aspectRatio} role="img">
								<VisualItem item={toCompareToMedia.current[index]?.[0]} size="large" />
							</GalleryItemDefaultStyle>
						) : (
							<div className="comparison-information" role="status" aria-live="polite">
								<Text tag="p" variant={{ type: 'copy', order: '2', style: 'normal' }}>
									{i18n('ngc.common.comparison.empty')}
								</Text>
							</div>
						)
					) : null
				}
			/>
		</div>
	);
};
